import { cn } from '@/lib/utils';
import { Transition } from '@headlessui/react';
import * as React from 'react';

interface FlyoutProps {
  children: React.ReactNode;
  open: boolean;
  setOpen: (next: boolean) => void;
  className?: string;
}

function Flyout({ children, open, setOpen, className }: FlyoutProps) {
  const panelContent = React.useRef<HTMLDivElement>(null);
  const closeBtn = React.useRef<HTMLButtonElement>(null);

  // close on click outside
  React.useEffect(() => {
    const clickHandler = ({ target }: { target: EventTarget | null }) => {
      if (!panelContent.current || !closeBtn.current) return;
      if (!open || panelContent.current.contains(target as Node) || closeBtn.current.contains(target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed
  React.useEffect(() => {
    const keyHandler = ({ keyCode }: { keyCode: number }): void => {
      if (!open || keyCode !== 27) return;
      setOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  return (
    <Transition
      show={open}
      unmount={false}
      as="div"
      id="order-details"
      ref={panelContent}
      className="absolute inset-0 sm:left-auto z-20 shadow-xl"
      enter="transition-transform duration-200 ease-in-out"
      enterFrom="translate-x-full"
      enterTo="translate-x-0"
      leave="transition-transform duration-200 ease-in-out"
      leaveFrom="translate-x-0"
      leaveTo="translate-x-full"
    >
      <div className="sticky top-16 bg-gradient-to-b from-gray-100 to-white dark:from-[#151D2C] dark:to-gray-900 overflow-x-hidden overflow-y-auto no-scrollbar shrink-0 border-l border-gray-200 dark:border-gray-700/60 w-full sm:w-[390px] h-[calc(100dvh-64px)]">
        <button
          ref={closeBtn}
          onClick={() => setOpen(false)}
          className="absolute top-0 right-0 mt-6 mr-6 group p-2 z-20"
        >
          <svg
            className="fill-gray-400 group-hover:fill-gray-600 pointer-events-none"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="m7.95 6.536 4.242-4.243a1 1 0 1 1 1.415 1.414L9.364 7.95l4.243 4.242a1 1 0 1 1-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 0 1-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 0 1 1.414-1.414L7.95 6.536Z" />
          </svg>
        </button>
        <div className="py-8 px-4 lg:px-8 h-full">{children}</div>
      </div>
    </Transition>
  );
}

function FlyoutSection({
  label,
  children,
  className,
}: {
  children: React.ReactNode;
  label?: string;
  className?: string;
}) {
  return (
    <div>
      {label && <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">{label}</h4>}
      <div className={cn('bg-white dark:bg-gray-800 rounded-xl p-4 text-sm space-y-3 drop-shadow-md', className)}>
        {children}
      </div>
    </div>
  );
}

function FlyoutRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between space-x-1">
      <span className="">{label}:</span>
      <span className="font-medium text-gray-700 dark:text-gray-100 text-right">{children}</span>
    </div>
  );
}

function FlyoutSectionDivider() {
  return (
    <div className="flex justify-between items-center" aria-hidden="true">
      <svg className="fill-white dark:fill-gray-800" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 20c5.523 0 10-4.477 10-10S5.523 0 0 0h20v20H0Z" />
      </svg>
      <div className="grow w-full h-5 bg-white dark:bg-gray-800 flex flex-col justify-center">
        <div className="h-px w-full border-t border-dashed border-gray-200 dark:border-gray-700" />
      </div>
      <svg
        className="fill-white dark:fill-gray-800 rotate-180"
        width="20"
        height="20"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M0 20c5.523 0 10-4.477 10-10S5.523 0 0 0h20v20H0Z" />
      </svg>
    </div>
  );
}

export { FlyoutSection, FlyoutRow, FlyoutSectionDivider, Flyout };
